import { isNegativeLevelForPriceNotation } from "@/lib/businessRules/levelValidation";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import {
  findBlocksUnderIcebergMin,
  getIcebergBlocksForTrade,
} from "../../helper/cellRenderers/IcebergCellRenderer/icebergMinSizeHelpers";
import tradeSettingsStore, {
  IIcebergCpCellToFlash,
  ILegCellToFlash,
  IOrderSettingFieldToFlash,
  ITradeSettingsStore,
} from "@/store/tradeSettingsStore";
import {
  ICPSettingsOverride,
  IToggleCounterpartiesButton,
} from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";

export function returnCounterpartiesAsStringCustomerCodes(
  counterpartiesList: IToggleCounterpartiesButton[],
): string[] {
  const updatedCPList = counterpartiesList.filter((cp) => {
    return cp.active;
  });
  const codeListOnly = updatedCPList.map((cp) => cp.code);

  return codeListOnly;
}

export function returnSelectedCounterpartyDesks(
  counterpartiesList: IToggleCounterpartiesButton[],
  CPCode: string,
) {
  let desksToReturn;
  counterpartiesList.map((e) => {
    if (CPCode == e.code) {
      if (
        (e.desks &&
          e.activeDesks &&
          e.activeDesks.length > 0 &&
          e.activeDesks.length < e.desks.length) ||
        (!e.desks && e.activeDesks && e.activeDesks.length > 0)
      ) {
        desksToReturn = e.activeDesks.map((desk) => desk);
        return [...desksToReturn];
      }
    }
  });
  return desksToReturn;
}

export function returnCounterpartiesAndDesks(
  counterpartiesList: IToggleCounterpartiesButton[],
) {
  const counteprartyCodes =
    returnCounterpartiesAsStringCustomerCodes(counterpartiesList);

  const counterpartiesAndDesksFormatted = counteprartyCodes.map(
    (counterparty) => {
      return {
        counterparty,
        deskRestriction: returnSelectedCounterpartyDesks(
          counterpartiesList,
          counterparty,
        ),
      };
    },
  );

  return [...counterpartiesAndDesksFormatted];
}

// Resolve a per-CP overridable setting: returns the CP-specific override if set, otherwise the global default.
export function resolveSettingForCP<K extends keyof ICPSettingsOverride>(
  thisTradeSettings: ITradeSettingsStore,
  cpCode: string,
  settingField: K,
): NonNullable<ICPSettingsOverride[K]> {
  const override = thisTradeSettings.cpSettingsOverrides?.[cpCode]?.[settingField];
  if (override !== undefined) return override as NonNullable<ICPSettingsOverride[K]>;
  return thisTradeSettings[settingField] as NonNullable<ICPSettingsOverride[K]>;
}

export function findThisTradeSettings({
  esid,
}: {
  esid: string;
}): ITradeSettingsStore | undefined {
  const allTradeSettings =
    tradeSettingsStore.getState().tradeSettingsData;
  const thisTradeSettings = allTradeSettings.find(
    (t) => t.esid === esid,
  )?.settings;

  // Set defaults
  return thisTradeSettings;
}

/**
 * LQC-2874: iceberg cells to flash because their % would release a block under
 * 100,000. A CP without an override flashes the trade-level cell it follows.
 */
export function findIcebergCellsUnderMinSize(
  esid: string,
): IIcebergCpCellToFlash[] {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) return [];
  const blocks = getIcebergBlocksForTrade(esid);

  const cells: IIcebergCpCellToFlash[] = [];
  const addCell = (cpCode?: string) => {
    if (cells.some((c) => c.cpCode === cpCode)) return;
    cells.push({ esid, cpCode, field: "releaseSize" });
  };

  thisTradeSettings.counterpartiesList
    .filter((cp) => cp.active)
    .forEach((cp) => {
      const percent = Number(
        resolveSettingForCP(thisTradeSettings, cp.code, "releaseSize"),
      );
      if (!findBlocksUnderIcebergMin(blocks, percent).length) return;
      const hasOverride =
        thisTradeSettings.cpSettingsOverrides?.[cp.code]?.releaseSize !==
        undefined;
      addCell(hasOverride ? cp.code : undefined);
    });
  return cells;
}

export interface IOrderLevelValidationResult {
  isValid: boolean;
  settingFieldsToFlash: IOrderSettingFieldToFlash[];
  legCellsToFlash: ILegCellToFlash[];
}

/**
 * Validates that at least one level value is populated when using "Leg" or "Spread / Legs" mode.
 * For Outright trades with "Set Level" = "Leg": checks orderPriceValue and leg referencePrice
 * For Structure trades with "Set Level" = "Spread / Legs": checks orderSpreadValue and leg referencePrice
 *
 * @param esid - The execution strategy ID
 * @param currentStoreTrade - The current trade from the store
 * @returns Validation result with fields that need to flash
 */
export function validateOrderLevelValues({
  esid,
  currentStoreTrade,
}: {
  esid: string;
  currentStoreTrade: ExecutionStrategy | undefined;
}): IOrderLevelValidationResult {
  const result: IOrderLevelValidationResult = {
    isValid: true,
    settingFieldsToFlash: [],
    legCellsToFlash: [],
  };

  const thisTradeSettings = findThisTradeSettings({ esid });

  if (!thisTradeSettings || !currentStoreTrade) {
    return result;
  }

  // Check if this is an Order trade type
  const isOrder =
    thisTradeSettings.tradeTypes.find((t) => t.active)?.name === "Order";

  if (!isOrder) {
    return result;
  }

  const strategyType = currentStoreTrade.strategyType;
  const isOutright = strategyType === StrategyType.Outright;
  const isStructure = strategyType === StrategyType.Structure;

  // Determine if we need validation based on Set Level setting
  let needsValidation = false;
  let settingFieldToCheck: "orderPriceValue" | "orderSpreadValue" | null = null;

  if (isOutright) {
    const setLevelOutright = thisTradeSettings.orderSetLevelOutright.find(
      (t) => t.active,
    )?.name;
    if (setLevelOutright === "Leg") {
      needsValidation = true;
      settingFieldToCheck = "orderPriceValue";
    }
  } else if (isStructure) {
    const setLevelStructure = thisTradeSettings.orderSetLevelStructure.find(
      (t) => t.active,
    )?.name;
    if (setLevelStructure === "Spread / Legs") {
      needsValidation = true;
      settingFieldToCheck = "orderSpreadValue";
    }
  }

  if (!needsValidation || !settingFieldToCheck) {
    return result;
  }

  // Check the setting input value (0 is a valid value)
  const settingValue = thisTradeSettings[settingFieldToCheck];
  const hasSettingValue =
    settingValue !== undefined && settingValue !== null;

  // Check leg referencePrice values from the trade's legs array
  const legs = currentStoreTrade.legs || [];

  let hasAnyLegValue = false;
  const legCellsToFlash: ILegCellToFlash[] = [];

  legs.forEach((leg) => {
    // 0 is a valid value for referencePrice
    const hasRefPrice =
      leg.referencePrice !== undefined && leg.referencePrice !== null;

    if (hasRefPrice) {
      hasAnyLegValue = true;
    } else {
      // This leg has no value, add it to the list to flash
      legCellsToFlash.push({
        esid,
        blockId: leg.blockId,
      });
    }
  });

  // If at least one value is populated, validation passes
  if (hasSettingValue || hasAnyLegValue) {
    return result;
  }

  // All values are empty - validation fails
  result.isValid = false;
  result.settingFieldsToFlash = [{ esid, field: settingFieldToCheck }];
  result.legCellsToFlash = legCellsToFlash;

  return result;
}

/**
 * LQC-896: validates that no Set Level value is negative for price notations
 * ($p, $fr) when sending an Order. Zero is allowed.
 *
 * Checks the settings-panel input (orderPriceValue) AND every leg's
 * referencePrice. Returns cells/fields to flash + an isValid flag.
 *
 * Only runs for Order trade type — RFQs are untouched.
 */
export function validateNoNegativeOrderLevels({
  esid,
  currentStoreTrade,
}: {
  esid: string;
  currentStoreTrade: ExecutionStrategy | undefined;
}): IOrderLevelValidationResult {
  const result: IOrderLevelValidationResult = {
    isValid: true,
    settingFieldsToFlash: [],
    legCellsToFlash: [],
  };

  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings || !currentStoreTrade) return result;

  const isOrder =
    thisTradeSettings.tradeTypes.find((t) => t.active)?.name === "Order";
  if (!isOrder) return result;

  const tradeNotation =
    currentStoreTrade.notationES || currentStoreTrade.notationER;

  // 1) Settings-panel Level input (Outright only — Structures use spread, not price)
  if (currentStoreTrade.strategyType === StrategyType.Outright) {
    if (
      isNegativeLevelForPriceNotation(
        thisTradeSettings.orderPriceValue,
        tradeNotation,
      )
    ) {
      result.settingFieldsToFlash.push({ esid, field: "orderPriceValue" });
    }
  }

  // 2) Each leg's referencePrice — flash the Set Level cell on offending legs
  const legs = currentStoreTrade.legs || [];
  legs.forEach((leg) => {
    const notation = leg.notation || tradeNotation;
    if (isNegativeLevelForPriceNotation(leg.referencePrice, notation)) {
      result.legCellsToFlash.push({
        esid,
        blockId: leg.blockId,
        field: "setLevel",
      });
    }
  });

  if (
    result.settingFieldsToFlash.length > 0 ||
    result.legCellsToFlash.length > 0
  ) {
    result.isValid = false;
  }

  return result;
}
